// SPDX-License-Identifier: AGPL-3.0-or-later
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import type {Guild} from '@app/features/guild/models/Guild';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import {ContentWarningLevel} from '@app/vendor/constants/GuildConstants';
import {msg} from '@lingui/core/macro';
import type {useLingui} from '@lingui/react/macro';
export type EffectiveSource = 'channel' | 'parent' | 'guild' | 'none';
export interface EffectiveContentWarning {
	level: number;
	text: string | null;
	source: EffectiveSource;
}
export interface EffectiveNsfwResult {
	value: boolean;
	source: EffectiveSource;
}
function getParentCategory(channel: Channel): Channel | null {
	if (channel.type === ChannelTypes.GUILD_CATEGORY) return null;
	if (!channel.parentId) return null;
	const parent = Channels.getChannel(channel.parentId);
	if (!parent) return null;
	return parent.type === ChannelTypes.GUILD_CATEGORY ? parent : null;
}
export function getEffectiveChannelNsfw(channel: Channel, guild: Guild | null | undefined): boolean {
	return resolveEffectiveChannelNsfw(channel, guild).value;
}
export function resolveEffectiveChannelNsfw(channel: Channel, guild: Guild | null | undefined): EffectiveNsfwResult {
	if (channel.nsfwOverride !== null && channel.nsfwOverride !== undefined) {
		return {value: channel.nsfwOverride, source: 'channel'};
	}
	const parent = getParentCategory(channel);
	if (parent && parent.nsfwOverride !== null && parent.nsfwOverride !== undefined) {
		return {value: parent.nsfwOverride, source: 'parent'};
	}
	if (guild) {
		return {value: guild.nsfw, source: 'guild'};
	}
	return {value: false, source: 'none'};
}
export function getEffectiveChannelContentWarning(
	channel: Channel,
	guild: Guild | null | undefined,
): EffectiveContentWarning {
	if (channel.contentWarningLevel !== ContentWarningLevel.INHERIT) {
		return {level: channel.contentWarningLevel, text: channel.contentWarningText, source: 'channel'};
	}
	const parent = getParentCategory(channel);
	if (parent && parent.contentWarningLevel !== ContentWarningLevel.INHERIT) {
		return {level: parent.contentWarningLevel, text: parent.contentWarningText, source: 'parent'};
	}
	if (guild && guild.contentWarningLevel !== ContentWarningLevel.INHERIT) {
		return {level: guild.contentWarningLevel, text: guild.contentWarningText, source: 'guild'};
	}
	return {level: ContentWarningLevel.INHERIT, text: null, source: 'none'};
}
export function getDefaultContentWarningText(i18n: ReturnType<typeof useLingui>['i18n']): string {
	return i18n._(msg`This contains sensitive content.`);
}
